import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { resolveAvatar } from "@/lib/api";
import { Loader, EmptyState } from "@/components/shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { key: "present", label: "Present", color: "bg-green-500", icon: "Check" },
  { key: "late", label: "Late", color: "bg-yellow-500", icon: "Clock" },
  { key: "excused", label: "Excused", color: "bg-blue-500", icon: "Shield" },
  { key: "absent", label: "Absent", color: "bg-red-500", icon: "X" },
];

export default function Attendance() {
  const qc = useQueryClient();
  const [sessionId, setSessionId] = useState("");
  const { data: sessions } = useQuery({ queryKey: ["sessions"], queryFn: async () => (await api.get("/sessions")).data });
  const { data: students, isLoading } = useQuery({ queryKey: ["att-students"], queryFn: async () => (await api.get("/users?role=student")).data });
  const { data: attendance, refetch } = useQuery({
    queryKey: ["attendance", sessionId], enabled: !!sessionId,
    queryFn: async () => (await api.get(`/attendance?session_id=${sessionId}`)).data,
  });

  useEffect(() => { if (sessions?.length && !sessionId) setSessionId(sessions[0].id); }, [sessions]);

  const attMap = {};
  (attendance || []).forEach((a) => { attMap[a.student_id] = a.status; });

  const mark = async (studentId, status) => {
    const r = await api.post("/attendance", { session_id: sessionId, student_id: studentId, status });
    if (r.data.points_awarded) toast.success(`Marked ${status} · +${r.data.points_awarded} pts`); else toast.success(`Marked ${status}`);
    refetch(); qc.invalidateQueries({ queryKey: ["students"] });
  };

  if (isLoading) return <Loader />;

  return (
    <div className="space-y-5" data-testid="attendance-page">
      <h2 className="font-head text-3xl text-csh-blue">Mark Attendance ✅</h2>
      <Select value={sessionId} onValueChange={setSessionId}>
        <SelectTrigger className="rounded-xl border-2 py-6 max-w-md" data-testid="session-select"><SelectValue placeholder="Choose a session" /></SelectTrigger>
        <SelectContent>
          {(sessions || []).map((s) => <SelectItem key={s.id} value={s.id}>W{s.week_number}S{s.session_number} · {s.title}</SelectItem>)}
        </SelectContent>
      </Select>

      {!sessionId ? <EmptyState icon="CalendarDays" title="Pick a session" /> : (
        <div className="space-y-2">
          {(students || []).map((st) => (
            <div key={st.id} className="flex flex-wrap items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-sm border border-black/5" data-testid={`att-row-${st.id}`}>
              <img src={resolveAvatar(st.avatar)} alt="" className="h-10 w-10 rounded-xl object-cover" />
              <span className="flex-1 font-bold text-csh-blue min-w-[120px]">{st.name}</span>
              <div className="flex gap-1.5">
                {STATUSES.map((s) => {
                  const I = Icons[s.icon]; const active = attMap[st.id] === s.key;
                  return (
                    <button key={s.key} onClick={() => mark(st.id, s.key)} data-testid={`mark-${s.key}-${st.id}`}
                      className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold transition-all ${active ? `${s.color} text-white` : "bg-black/5 text-csh-grey hover:bg-black/10"}`}>
                      <I size={13} /> {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
